import { of, from, Observable } from 'rxjs';
import { flatMap } from 'rxjs/operators';
import { getUrl } from '@/common/oldMobile';
import axios from 'axios';

class ImageUtils {
    static _readFileAsUrl(im) {
        const file = im.get('file');
        return Observable.create(observer => {
            let reader = new FileReader();
            reader.onload = event => {
                observer.next(im.setIn(['image', 'url'], event.target.result));
                observer.complete();
            };
            reader.onerror = () => observer.error(im);
            reader.onabort = () => observer.error(im);
            reader.readAsDataURL(file);
        });
    }

    static _getImage(im) {
        const url = im.getIn(['image', 'url']);
        return Observable.create(observer => {
            const image = new Image();
            image.onload = () => {
                observer.next(im.setIn(['image', 'imageObject'], image));
                observer.complete();
            };
            image.onerror = () => observer.error(im);
            image.src = url;
        });
    }

    static _resizeImage(im) {
        const file = im.get('file');
        const image = im.getIn(['image', 'imageObject']);

        const maxSizeMB = 1;
        const maxWidth = 1024;
        const maxHeight = 768;
        const quality = 0.7;

        const canvasToBlob = function(canvas, type) {
            let byteString = atob(canvas.split(',')[1]),
                ab = new ArrayBuffer(byteString.length),
                ia = new Uint8Array(ab),
                i;
            for (i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }
            return new Blob([ab], {
                type: type
            });
        };
        return Observable.create(observer => {
            let fileType = file.type;
            let fileSize = file.size;
            let width = image.width;
            let height = image.height;
            const maxSizeB = parseInt(maxSizeMB, 10) > 0 ? maxSizeMB * 1000000 : 0;
            if (maxSizeB < fileSize) {
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }
            }
            let canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(image, 0, 0, width, height);
            const resizedData = {};
            resizedData.url = canvas.toDataURL(fileType, quality);
            resizedData.blob = canvasToBlob(resizedData.url, fileType);
            resizedData.type = fileType;
            observer.next(im.setIn(['image', 'resized'], resizedData));
            observer.complete();
        });
    }
}

export class UploadUtils {
    static upload(im, { idZak, slotName, documentId, page, clientHash }, progressCallback) {
        const resized = im.getIn(['image', 'resized']);

        const url = getUrl('UPLOAD_DOCUMENT');
        const formData = new FormData();
        if (resized) {
            formData.append('file', resized.blob, im.get('filename'), { type: resized.type });
        } else {
            const file = im.get('file');
            formData.append('file', file, im.get('filename'));
        }
        formData.append('slotName', slotName);
        formData.append('documentId', documentId);
        formData.append('page', page);
        formData.append('idZak', idZak);
        formData.append('clientHash', clientHash);

        return from(
            axios.request({
                url: url,
                data: formData,
                method: 'POST',
                onUploadProgress: function(progressEvent) {
                    const progress = (progressEvent.loaded / progressEvent.total) * 100;
                    if (progressCallback) {
                        progressCallback(progress);
                    }
                }
            })
        );
    }

    static determineFileType(im) {
        const file = im.get('file');
        return Observable.create(observer => {
            let resolved = null;
            if (/(jpe?g|png|gif)$/i.test(file.type)) {
                resolved = 'image';
            }
            if (/(pdf)$/i.test(file.type)) {
                resolved = 'pdf';
            }
            if (!resolved) {
                observer.error(im);
            }
            observer.next(im.set('resolvedType', resolved).set('filename', Date.now() + '_' + file.name));
            observer.complete();
        });
    }

    static resizeIfImage(im) {
        const next = of(im);
        if (im.get('resolvedType') === 'image') {
            return next.pipe(
                flatMap(ImageUtils._readFileAsUrl),
                flatMap(ImageUtils._getImage),
                flatMap(ImageUtils._resizeImage)
            );
        }
        return next;
    }
}
